import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FiArrowRight, FiCheckCircle, FiClock, FiFacebook, FiInstagram, FiLinkedin, FiMail, FiMapPin, FiPhone, FiSend, FiTwitter, FiUpload, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import apiService from '../services/api';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    cv: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [submitMessage, setSubmitMessage] = useState('');
  const [errors, setErrors] = useState({});

  // API endpoint
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + "/api";

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      
      // Validate file
      if (file) {
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/rtf'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        
        if (!allowedTypes.includes(file.type)) {
          setErrors(prev => ({
            ...prev,
            cv: 'Only PDF, DOC, DOCX, TXT, and RTF files are allowed.'
          }));
          return;
        }
        
        if (file.size > maxSize) {
          setErrors(prev => ({
            ...prev,
            cv: 'File size must be less than 5MB.'
          }));
          return;
        }
        
        // Clear CV error if file is valid
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.cv;
          return newErrors;
        });
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear field error when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    console.log('Validating form with data:', formData);
    
    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      console.log('Name validation failed');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
      console.log('Name too short');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      console.log('Email validation failed');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      console.log('Email format invalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      console.log('Message validation failed');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
      console.log('Message too short');
    }
    
    // Optional phone validation
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
      console.log('Phone validation failed');
    }
    
    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started');
    console.log('Form data:', formData);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Form validation passed');
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append form fields
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('company', formData.company.trim());
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message.trim());
      
      // Append CV file if selected
      if (formData.cv) {
        formDataToSend.append('cv', formData.cv);
      }
      
      console.log('Sending request to:', `${API_BASE_URL}/contact`);
      console.log('FormData entries:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }
      
      // Submit to backend
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        body: formDataToSend,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      const result = await response.json();
      console.log('Response data:', result);
      
      if (response.ok) {
        // Success
        setSubmitStatus('success');
        setSubmitMessage(result.message || 'Thank you for contacting us! We will get back to you within 24 hours.');
        
        // Track successful contact form submission
        apiService.trackEvent({
          event: 'contact_form',
          data: {
            service: formData.service,
            hasCV: !!formData.cv
          }
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          cv: null
        });
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
        
        console.log('Contact submitted successfully:', result.data);
      } else {
        // Error
        setSubmitStatus('error');
        
        if (result.errors && Array.isArray(result.errors)) {
          // Validation errors
          const fieldErrors = {};
          result.errors.forEach(error => {
            fieldErrors[error.path || error.param] = error.msg;
          });
          setErrors(fieldErrors);
          setSubmitMessage('Please correct the errors below and try again.');
        } else {
          setSubmitMessage(result.message || 'An error occurred while submitting the form. Please try again.');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Debug function to test API directly
  const testAPI = async () => {
    console.log('Testing API directly...');
    
    try {
      const testData = new FormData();
      testData.append('name', 'Test User');
      testData.append('email', 'test@example.com');
      testData.append('message', 'This is a test message');
      
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        body: testData,
      });
      
      const result = await response.json();
      console.log('API Test Result:', result);
      
      if (response.ok) {
        alert('API test successful!');
      } else {
        alert('API test failed: ' + result.message);
      }
    } catch (error) {
      console.error('API test error:', error);
      alert('API test network error: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const contactInfo = [
    
    {
      icon: FiMail,
      title: "Email",
      details: ["Connect@innofusionanalytics.com ", "Info@innofusionanalytics.com"],
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: FiMapPin,
      title: "Headquarters",
      details: ["United States"],
      color: "from-green-500 to-blue-600"
    },
    {
      icon: FiMapPin, 
      title: "Office",
      details: ["India"],
      color: "from-orange-500 to-red-600"
    },
    {
      icon: FiPhone,
      title: "Call",
      details: ["+1 (657) 867 7883"],
      color: "from-red-500 to-purple-600"
    },
  ];

  const services = [
    "IT Staffing",
    "Project Services",
    "Project Management",
    "Enterprise Applications",
    "Cloud & Infrastructure",
    "Business Process Management",
    "Business Transformation",
    "AI & Intelligent Agent Development",
    "Cyber Security"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            <motion.div variants={itemVariants}>
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
                Get In Touch
              </span>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
                Let's Build Something
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                  Extraordinary Together
                </span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
                Ready to transform your business with expert talent? We'd love to hear about your project 
                and discuss how Innofusion can help you achieve your goals.
              </p>
            </motion.div>

            {/* Attractive Client Intake Form Button */}
            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 p-8 rounded-3xl shadow-2xl border border-purple-200 relative overflow-hidden">
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-indigo-600/20 animate-pulse"></div>
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent"></div>
                
                <div className="relative z-10 space-y-4">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                    className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <span className="text-3xl">✨</span>
                  </motion.div>
                  
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 font-display tracking-tight">
                    Start Your Project Journey
                  </h3>
                  <p className="text-purple-100 text-lg mb-6 font-body max-w-2xl mx-auto">
                    Tell us about your vision, goals, and requirements through our comprehensive intake form. 
                    Get personalized solutions tailored to your business needs.
                  </p>
                  
                  <Link to="/client-intake">
                    <motion.button
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                        y: -5
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="group bg-white text-purple-600 px-10 py-4 rounded-full font-bold text-xl shadow-2xl hover:shadow-purple-500/25 transition-all duration-500 relative overflow-hidden font-accent tracking-wide my-4"
                    >
                      <span className="relative z-10 flex items-center space-x-3">
                        <span>Complete Client Intake Form</span>
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <FiArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                        </motion.div>
                      </span>
                      
                      {/* Animated background on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    </motion.button>
                  </Link>

                  <div className="flex items-center justify-center space-x-6 mt-6 text-purple-100">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-body">Free Consultation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse animation-delay-1000"></div>
                      <span className="text-sm font-body">24/7 Response</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse animation-delay-2000"></div>
                      <span className="text-sm font-body">Tailored Solutions</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">Send Us a Message</h2>
                  <p className="text-gray-600 font-body font-light tracking-wide">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
                  >
                    <FiCheckCircle className="text-green-500 flex-shrink-0" size={20} />
                    <p className="text-green-700 font-medium">{submitMessage}</p>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
                  >
                    <FiX className="text-red-500 flex-shrink-0" size={20} />
                    <p className="text-red-700 font-medium">{submitMessage}</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-7">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body ${
                          errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="John Doe"
                        disabled={isSubmitting}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body ${
                          errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="john@company.com"
                        disabled={isSubmitting}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Contact Number (Optional)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body ${
                          errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="+1 (555) 123-4567"
                        disabled={isSubmitting}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Attach your CV (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          name="cv"
                          onChange={handleChange}
                          accept=".pdf,.doc,.docx,.txt,.rtf"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${
                            errors.cv ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          disabled={isSubmitting}
                        />
                        {formData.cv && (
                          <div className="mt-2 text-sm text-green-600 flex items-center space-x-2">
                            <FiUpload size={16} />
                            <span>File selected: {formData.cv.name} ({formatFileSize(formData.cv.size)})</span>
                          </div>
                        )}
                        {errors.cv && (
                          <p className="mt-1 text-sm text-red-600">{errors.cv}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                        placeholder="Your Company"
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                        Service Interested In
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body"
                        disabled={isSubmitting}
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 font-heading tracking-wide">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 font-body ${
                        errors.message ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="Tell us about your project and requirements..."
                      disabled={isSubmitting}
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className={`w-full px-8 py-4 rounded-lg font-semibold shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 font-accent tracking-wide ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:shadow-xl'
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <FiSend />
                        <span>Send Message</span>
                      </>
                    )}
                  </motion.button>

                 
                </form>
              </div>
            </motion.div>

            {/* Office Info & Social */}
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Office Image/Map Placeholder */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-white/10 rounded-3xl backdrop-blur-sm"></div>
                <div className="relative z-10">
                  <div className="flex items-center space-x-3 mb-6">
                    <FiMapPin size={24} />
                    <h3 className="text-2xl font-bold font-display tracking-tight">Our Office</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2 font-heading tracking-wide">Headquarters</h4>
                      <p className="text-blue-100 font-body">
                      United States<br /> </p>
                      <h4 className="font-semibold mb-2 mt-6 font-heading tracking-wide">Office</h4>
                        India<br />
                       
                     
                    </div>
                   
                  </div>
                </div>
              </div>

              {/* Social Media & Additional Info */}
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 font-display tracking-tight">Connect With Us</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: FiLinkedin, label: "LinkedIn", color: "from-blue-600 to-blue-700", href: "https://www.linkedin.com/company/innofusion-analytics-llc/" },
                    { icon: FiTwitter, label: "Twitter", color: "from-sky-500 to-blue-600", href: "https://x.com/InnoFusion_IFA?t=TX41SiD-OrpOH4xF2pkfmA&s=09" },
                    { icon: FiFacebook, label: "Facebook", color: "from-indigo-500 to-purple-600", href: "https://www.facebook.com/innofusionanalytics" },
                    { icon: FiInstagram, label: "Instagram", color: "from-purple-500 to-pink-600", href: "https://www.instagram.com/innofusionanalytics?utm_source=qr&igsh=MWkyZTdwaXd5azQyeg==" }
                  ].map((social, index) => (
                   <Link to={social.href} target="_blank"><motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center space-x-3 p-4 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                    >
                      <div className={`w-10 h-10 bg-gradient-to-br ${social.color} rounded-lg flex items-center justify-center`}>
                        <social.icon className="text-white" size={20} />
                      </div>
                      <span className="font-medium text-gray-700 font-body">{social.label}</span>
                    </motion.div></Link>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-semibold text-gray-900 mb-3 font-heading tracking-wide">Why Choose Innofusion?</h4>
                  <ul className="space-y-2 text-sm text-gray-600 font-body">
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>50+ Successful Projects Delivered</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      <span>50+ Expert Consultants Available</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span>98% Client Satisfaction Rate</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                      <span>24/7 Support & Consultation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-100 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <info.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-heading tracking-wide">{info.title}</h3>
                  <div className="space-y-2">
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 font-body text-sm">{detail}</p>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact; 